import { Button, Tag } from "@design";
import { useQuery } from "@tanstack/react-query";
import { Wrench } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { listToolsOptions } from "@/api";
import { Markdown } from "@/components/chat/Markdown";
import { Text } from "@/components/Text";
import { DraftCard } from "../DraftCard";
import { useDraftContext } from "../DraftContext";
import type { EntityDraftCardProps } from "../registry";
import { useSkillDraft } from "./useSkillDraft";

const PREVIEW = 1_200;

/** A skill draft: what it's for, its instructions (rendered), the tools it needs. */
export function SkillDraftCard({ draft, state, entityUuid, updatedAt, answerable }: Readonly<EntityDraftCardProps>) {
  const { t } = useTranslation("buildChat");
  const { linked } = useDraftContext();
  const skill = useSkillDraft(draft);
  const [all, setAll] = useState(false);
  const toolUuids = skill.body.requiredToolUuids ?? [];
  const tools = useQuery({
    ...listToolsOptions({ query: { page_size: 100 } }),
    select: (r) => r.data,
    enabled: toolUuids.length > 0,
  });
  const instructions = skill.body.instructions ?? "";
  const long = instructions.length > PREVIEW;
  const labels = [skill.body.category, ...(skill.body.tags ?? [])].filter((label): label is string => Boolean(label));
  return (
    <DraftCard
      title={skill.body.name ?? linked.find((e) => e.uuid === draft.targetUuid)?.name ?? t("draft.untitled")}
      entityLabel={t("draft.entity.skill")}
      state={state}
      isUpdate={Boolean(draft.targetUuid)}
      doneUuid={skill.doneUuid}
      entityUuid={entityUuid}
      updatedAt={updatedAt}
      missingFields={draft.missingFields}
      questions={draft.questions}
      answerable={answerable}
      busy={skill.busy}
      steps={skill.steps}
      onCreate={skill.run}
      onRetry={skill.retry}
      onOpenInForm={skill.openInForm}
      detailPath={(uuid) => `/build/skills/${uuid}`}
      summary={
        <div className="space-y-2">
          {skill.body.description && <Text variant="body-sm">{skill.body.description}</Text>}
          {labels.length > 0 && (
            <div className="flex flex-wrap gap-1.5">
              {labels.map((label) => (
                <Tag key={label}>{label}</Tag>
              ))}
            </div>
          )}
          {instructions && (
            <div className="space-y-1 rounded-lg border border-border-subtle p-3">
              <Markdown>{all || !long ? instructions : `${instructions.slice(0, PREVIEW)}…`}</Markdown>
              {long && (
                <Button variant="ghost" size="sm" onClick={() => setAll((open) => !open)}>
                  {all ? t("skill.showLess") : t("skill.showAll")}
                </Button>
              )}
            </div>
          )}
          {toolUuids.length > 0 && (
            <div className="flex flex-wrap items-center gap-1.5">
              <Text variant="caption" tone="tertiary">
                {t("skill.needs")}
              </Text>
              {toolUuids.map((uuid) => (
                <Tag key={uuid} icon={<Wrench size={12} />}>
                  {tools.data?.find((tool) => tool.uuid === uuid)?.name ?? uuid}
                </Tag>
              ))}
            </div>
          )}
        </div>
      }
    />
  );
}
